import { useCallback, useEffect, useRef, useState } from 'react';

import { binanceSocketManager } from '../lib/binanceSocket';
import { normalizeBinanceTicker } from '../lib/normalize';
import { useRealtimeGaveUpToast } from './useRealtimeGaveUpToast';

import type { NormalizedTicker } from '../types/exchange';

interface UseBinanceWebSocketProps {
  symbols: string[]; // e.g. ['btcusdt', 'ethusdt']
  onTickerUpdate: (ticker: NormalizedTicker) => void;
  enabled?: boolean;
}

export const parseBinanceMessage = (data: string): NormalizedTicker | null => {
  try {
    const parsed = JSON.parse(data);

    // subscribe 응답 무시: { result: null, id: 1 }
    if ('result' in parsed && 'id' in parsed) return null;

    // 개별 스트림 메시지: { e: '24hrTicker', s: 'BTCUSDT', ... }
    if (parsed.e === '24hrTicker') {
      return normalizeBinanceTicker({
        s: parsed.s,
        c: parsed.c,
        o: parsed.o,
        h: parsed.h,
        l: parsed.l,
        x: parsed.x,
        p: parsed.p,
        P: parsed.P,
        v: parsed.v,
        q: parsed.q,
      });
    }

    // 복합 스트림 메시지: { stream: 'btcusdt@ticker', data: { ... } }
    if (parsed.data?.e === '24hrTicker') {
      return normalizeBinanceTicker({
        s: parsed.data.s,
        c: parsed.data.c,
        o: parsed.data.o,
        h: parsed.data.h,
        l: parsed.data.l,
        x: parsed.data.x,
        p: parsed.data.p,
        P: parsed.data.P,
        v: parsed.data.v,
        q: parsed.data.q,
      });
    }

    return null;
  } catch (error) {
    // 🔴 raw error를 삼키지 않는다(TRAD-3045 조사에서 지적된 패턴).
    console.error('[useBinanceWebSocket] ticker parse failed', error);
    return null;
  }
};

export const useBinanceWebSocket = ({ symbols, onTickerUpdate, enabled = true }: UseBinanceWebSocketProps) => {
  const onTickerUpdateRef = useRef(onTickerUpdate);
  const [isConnected, setIsConnected] = useState(false);

  useRealtimeGaveUpToast(binanceSocketManager, enabled && symbols.length > 0);

  useEffect(() => {
    onTickerUpdateRef.current = onTickerUpdate;
  }, [onTickerUpdate]);

  const handleMessage = useCallback(async (data: string | ArrayBuffer | Blob) => {
    const text = typeof data === 'string' ? data : data instanceof Blob ? await data.text() : '';
    const ticker = parseBinanceMessage(text);
    if (ticker) {
      onTickerUpdateRef.current(ticker);
    }
  }, []);

  useEffect(() => {
    if (!enabled || symbols.length === 0) return;

    const unsubscribe = binanceSocketManager.subscribe(
      'ticker',
      symbols.map(s => s.toLowerCase()),
      handleMessage,
    );
    const unlistenConnection = binanceSocketManager.onConnectionChange(setIsConnected);
    setIsConnected(binanceSocketManager.connected);

    return () => {
      unsubscribe();
      unlistenConnection();
    };
  }, [enabled, symbols.join(','), handleMessage]);

  return { isConnected };
};
